import {
  CartItemOptions,
  CartItemPayload,
  CartUpdateItemReqBody,
} from '@sola/schema/dotcom/cart/api.ts';
import {
  CartItem,
  CartItemRelEnum,
  CartItemTypeEnum,
} from '@sola/schema/dotcom/cart/cart-item.ts';
import { Cart, CartUpsellActionEnum } from '@sola/schema/dotcom/cart/cart.ts';
import { CartItemPaymentPayload } from '@sola/schema/dotcom/cart/payment.ts';
import {
  UpsellItem,
  UpsellItemTypeEnum,
} from '@sola/schema/dotcom/checkout/upsell.ts';
import {
  sendCartAddEvent,
  sendCartRemoveEvent,
} from '@sola/shared-utils/analytics-events.ts';
import {
  isItemFund,
  isItemSponsorship,
} from '@sola/shared-utils/cart/cart-item-type-guards.ts';

import {
  addItemsRequest,
  getCartRequest,
  getRestoredCartRequest,
  logoutRequest,
  removeItemRequest,
  updateItemRequest,
  updatePaymentItemsRequest,
  updateUpsellRequest,
} from './requests.ts';
import { getUpsellSponsorshipItem } from './utils/upsells.ts';

/**
 * Format cart items based on their item types.
 */
const formatItems = (
  items: CartItemPayload[],
  options?: CartItemOptions,
): CartItemPayload[] => {
  return items.map((item): CartItemPayload => {
    switch (item.itemType) {
      case CartItemTypeEnum.Fund:
      case CartItemTypeEnum.Gift:
        return {
          ...item,
          ...options,
        };
      default:
        return item;
    }
  });
};

/**
 * Order items
 */
export const sortItems = (cartItems: CartItem[]) => {
  return cartItems.sort((item1, item2) => {
    // Although not necessary, getTime() plays better with TS
    return new Date(item2.added).getTime() - new Date(item1.added).getTime();
    // return item1.itemType === CartItemTypeEnum.Sponsorship ? -1 : 0
  });
};

/**
 * Check for and retrieve current cart.
 */
export const fetchCart = async (
  queryString?: Record<string, string>,
): Promise<Cart | null | undefined> => {
  // See if we have a cart in session.
  // const localCart = getCartFromStorage();
  // if (localCart) {
  // 	return localCart;
  // }

  // TODO: Remove this commented code. Just documenting that our SDKs (client-side) don't have access to the cartId as we make the cookie HttpOnly.
  //		 However, we don't need the application to read the cookie - requests to the cart API will include the cookie
  // const existingCartId = getPersistedCartID();
  // if (!existingCartId) {
  // 	return null;
  // }

  const cart = await getCartRequest(undefined, queryString);
  if (!cart) {
    return null;
  }

  // saveCartSession();

  // TODO: Remove this commented code. We don't want the SDK writing the cart cookie - only the API
  // Make sure to add it to the current session.
  // if (cart) {
  // 	updateCartStorage(cart);
  // }

  return cart;
};

/*
 * Add item(s)
 */
export const addToCart = async ({
  items,
  options,
  supporterId,
  referrer,
}: {
  items: CartItemPayload | CartItemPayload[];
  options?: CartItemOptions;
  supporterId?: string;
  referrer?: string;
}): Promise<Cart | undefined> => {
  const itemsArray = Array.isArray(items) ? items : [items];
  const formattedItems = formatItems(itemsArray, options);
  const updatedCart = await addItemsRequest(
    formattedItems,
    supporterId,
    referrer,
  );

  // saveCartSession();

  const updatedItems = updatedCart?.items.filter(
    (item) => item.added === updatedCart.version,
  );

  if (updatedCart && updatedItems) {
    sendCartAddEvent(updatedItems, updatedCart.id);
  }

  return updatedCart;
};

/**
 * Update an item already in cart.
 */
export const updateItem = async (
  id: CartItem['id'],
  updates: CartUpdateItemReqBody,
): Promise<Cart | void> => {
  // TODO: Guessing we'll need some better handling of errors here
  return await updateItemRequest(id, updates);
};

/**
 * Update payment items in the cart in a single request.
 */
export const updatePaymentItems = async ({
  cartId,
  paymentItems,
}: {
  cartId?: Cart['id'];
  paymentItems: CartItemPaymentPayload[];
}): Promise<Cart | void> => {
  const updatedCart = await updatePaymentItemsRequest(paymentItems, cartId);

  if (updatedCart?.removedItems) {
    sendCartRemoveEvent(updatedCart.removedItems);
  }

  const addedItems = updatedCart?.items.filter(
    (item) => item.added === updatedCart.version,
  );

  if (updatedCart && addedItems) {
    sendCartAddEvent(addedItems, updatedCart.id);
  }

  return updatedCart;
};

/**
 * Remove item(s) based on id.
 */
export const removeFromCart = async (
  id: CartItem['id'],
): Promise<Cart | void> => {
  // TODO: Guessing we'll need some better handling of errors here
  const updatedCart = await removeItemRequest(id);

  if (updatedCart?.removedItems) {
    sendCartRemoveEvent(updatedCart.removedItems);
  }

  return updatedCart;
};

/**
 * Remove item(s) based on id.
 */
export const removeFromCartId = async (
  cartId: Cart['id'],
  itemId: CartItem['id'],
): Promise<Cart | void> => {
  // TODO: Guessing we'll need some better handling of errors here
  const updatedCart = await removeItemRequest(itemId, cartId);

  return updatedCart;
};

/**
 * Deletes items that require authentication from existing cart
 */
export const logOut = async (): Promise<Cart | undefined> => {
  // TODO: Guessing we'll need some better handling of errors here
  return await logoutRequest();
};

/**
 * Get a cart upsell item based on the `itemType` requested
 */
export const getUpsell = async (
  itemType: UpsellItemTypeEnum,
  cart?: Cart | null,
): Promise<{
  upsellItem: UpsellItem | null | undefined;
  lastItem?: CartItem;
}> => {
  // Only presenting upsells when the last item added is a fund
  if (!cart || !cart.items || cart.items.length === 0) {
    return { upsellItem: null };
  }

  // Don't present upsells if there are any sponsorships already in the cart
  if (cart.items.some(isItemSponsorship)) {
    return { upsellItem: null };
  }

  const lastItem = cart.items.reduce((laterItem, item) => {
    return item.added > laterItem.added ? item : laterItem;
  }, cart.items[0]);

  if (!isItemFund(lastItem)) {
    return { upsellItem: null };
  }

  if (
    !cart.upsell ||
    !cart.upsell.canOffer ||
    cart.upsell.declinedIds.includes(lastItem.id)
  ) {
    return { upsellItem: null };
  }

  switch (itemType) {
    /**
     * Used for child upsell item type
     */
    case UpsellItemTypeEnum.Sponsorship: {
      return { upsellItem: await getUpsellSponsorshipItem(), lastItem };
    }

    // /**
    //  * Used for Fund upsell item type
    //  */
    // case UpsellItemTypeEnum.Fund:
    // 	return await getUpsellFundItem()
  }
};

/**
 * @description Increment the cart upsell accepted count
 */
export const updateUpsell = async ({
  action,
  itemId,
}: {
  action: CartUpsellActionEnum;
  itemId?: string;
}): Promise<Cart | void> => {
  return updateUpsellRequest({ action, itemId });
};

/**
 * Get the related item by relationship.
 */
export const getRelatedItem = (
  parent: CartItem,
  rel: CartItemRelEnum,
  cart: Cart | null,
): CartItem | undefined => {
  if (!parent || !cart) {
    return undefined;
  }

  return cart.items?.find((item) => {
    return item.rel === rel && item.parentId === parent.id;
  });
};

/**
 * Restore cart (including merge with current cart)
 */
export const fetchRestoredCart = async (
  restoreCartId: string,
): Promise<Cart | null | undefined> => {
  const cart = await getRestoredCartRequest(restoreCartId);
  // saveCartSession();

  return cart;
};

/**
 * Check to determine if item is in error
 */
export const isItemInError = (item: CartItem, cart: Cart | null): boolean => {
  if (!cart?.errors?.length) {
    return false;
  }

  return cart.errors.some((err) => err.id === item.id);
};
